import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { adminApi } from "../lib/adminApi";
import { PageHeader } from "./formKit";
import { Archive, RefreshCw, ChevronDown } from "lucide-react";

const STATUSES = [
  "new",
  "contacted",
  "consultation booked",
  "not a fit",
  "enrolled",
  "archived",
];

const STATUS_STYLES = {
  new: "bg-sky-50 text-[#0369A1] border-sky-200",
  contacted: "bg-amber-50 text-amber-800 border-amber-200",
  "consultation booked": "bg-violet-50 text-violet-800 border-violet-200",
  "not a fit": "bg-slate-100 text-slate-600 border-slate-200",
  enrolled: "bg-emerald-50 text-emerald-800 border-emerald-200",
  archived: "bg-slate-100 text-slate-500 border-slate-200",
};

export default function AdminInquiriesPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [expanded, setExpanded] = useState({});

  const refresh = async () => {
    setLoading(true);
    try {
      const params = filter && filter !== "all" ? { status: filter } : {};
      const { data } = await adminApi.get("/admin/inquiries", { params });
      setItems(data || []);
    } catch {
      toast.error("Couldn't load inquiries.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const updateStatus = async (id, status) => {
    try {
      await adminApi.patch(`/admin/inquiries/${id}`, { status });
      toast.success(`Status set to "${status}".`);
      await refresh();
    } catch {
      toast.error("Couldn't update status.");
    }
  };

  const archive = (id) => updateStatus(id, "archived");

  const counts = useMemo(() => {
    const c = { all: items.length };
    STATUSES.forEach((s) => (c[s] = items.filter((i) => i.status === s).length));
    return c;
  }, [items]);

  const toggle = (id) => setExpanded((e) => ({ ...e, [id]: !e[id] }));

  return (
    <div data-testid="admin-inquiries-page">
      <PageHeader
        title="Inquiries"
        subtitle="Inquiries submitted from the public website. Update status to keep your pipeline organized."
        actions={
          <button
            onClick={refresh}
            className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-4 py-2 text-xs font-bold text-slate-700 hover:bg-slate-50"
            data-testid="inquiries-refresh"
          >
            <RefreshCw className="h-3.5 w-3.5" /> Refresh
          </button>
        }
      />

      <div className="flex flex-wrap gap-2 mb-6" data-testid="inquiries-filter">
        {["all", ...STATUSES].map((s) => (
          <button
            key={s}
            onClick={() => setFilter(s)}
            className={`pill border transition-all ${
              filter === s
                ? "bg-slate-900 text-white border-slate-900"
                : "bg-white text-slate-700 border-slate-200 hover:border-slate-300"
            }`}
            data-testid={`inquiries-filter-${s.replace(/\s+/g, "-")}`}
          >
            {s}
            {filter === "all" && s !== "all" && (
              <span className="ml-1.5 opacity-70 font-bold">
                {counts[s] || 0}
              </span>
            )}
          </button>
        ))}
      </div>

      {loading ? (
        <p className="text-sm text-slate-500">Loading…</p>
      ) : items.length === 0 ? (
        <div className="rounded-2xl bg-white border border-dashed border-slate-300 p-10 text-center text-slate-500 text-sm">
          No inquiries yet.
        </div>
      ) : (
        <ul className="grid gap-3" data-testid="inquiries-list">
          {items.map((i) => {
            const open = !!expanded[i.id];
            return (
              <li
                key={i.id}
                className="rounded-2xl bg-white border border-slate-200 overflow-hidden"
                data-testid={`inquiry-row-${i.id}`}
              >
                <div className="p-4 sm:p-5 grid sm:grid-cols-[1fr_auto] gap-3 items-start">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span
                        className={`pill border ${
                          STATUS_STYLES[i.status] ||
                          "bg-slate-100 border-slate-200 text-slate-700"
                        }`}
                        data-testid={`inquiry-status-${i.id}`}
                      >
                        {i.status}
                      </span>
                      <p className="font-display font-bold text-slate-900 truncate">
                        {i.parent_name}
                      </p>
                      <span className="text-xs text-slate-500">
                        {i.email}
                        {i.phone ? ` · ${i.phone}` : ""}
                      </span>
                    </div>
                    <p className="text-xs text-slate-500 mt-1">
                      {i.child_name ? `${i.child_name} (age ${i.child_age || "—"})` : "—"}
                      {i.support_needs?.length
                        ? ` · ${i.support_needs.slice(0, 3).join(", ")}${
                            i.support_needs.length > 3 ? "…" : ""
                          }`
                        : ""}
                      {i.source_page ? ` · from ${i.source_page}` : ""}
                    </p>
                    <p className="text-[11px] text-slate-400 mt-1">
                      Received {new Date(i.created_at).toLocaleString()}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap justify-end">
                    <div className="relative">
                      <select
                        value={i.status}
                        onChange={(e) => updateStatus(i.id, e.target.value)}
                        className="appearance-none rounded-full border border-slate-200 bg-white pl-3 pr-8 py-1.5 text-xs font-bold text-slate-700 focus:outline-none focus:border-slate-400 cursor-pointer"
                        data-testid={`inquiry-set-status-${i.id}`}
                      >
                        {STATUSES.map((s) => (
                          <option key={s} value={s}>
                            {s}
                          </option>
                        ))}
                      </select>
                      <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-500 pointer-events-none" />
                    </div>
                    {i.status !== "archived" && (
                      <button
                        onClick={() => archive(i.id)}
                        className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 px-3 py-1.5 text-xs font-bold text-slate-700 hover:bg-slate-50"
                        data-testid={`inquiry-archive-${i.id}`}
                      >
                        <Archive className="h-3.5 w-3.5" />
                        Archive
                      </button>
                    )}
                    <button
                      onClick={() => toggle(i.id)}
                      className="inline-flex items-center gap-1 rounded-full bg-slate-900 text-white px-3 py-1.5 text-xs font-bold"
                      data-testid={`inquiry-toggle-${i.id}`}
                    >
                      {open ? "Hide" : "Details"}
                    </button>
                  </div>
                </div>

                {open && (
                  <div
                    className="border-t border-slate-100 px-4 sm:px-5 py-4 grid sm:grid-cols-2 gap-3 text-sm bg-slate-50/60"
                    data-testid={`inquiry-details-${i.id}`}
                  >
                    {[
                      ...(i.inquiry_type
                        ? [["Inquiry type", i.inquiry_type === "trial_class" ? "Trial Class" : i.inquiry_type]]
                        : []),
                      ...(i.selected_age_range
                        ? [["Age range (funnel)", i.selected_age_range]]
                        : []),
                      ...(i.selected_interests?.length
                        ? [["Interests (funnel)", i.selected_interests.join(", ")]]
                        : []),
                      ...(i.selected_program_name
                        ? [["Selected program", i.selected_program_name]]
                        : []),
                      ...(i.recommendation_reason
                        ? [["Why recommended", i.recommendation_reason]]
                        : []),
                      ["Diagnosis / support need", i.diagnosis_or_support_need],
                      ["Autism Funding", i.autism_funding],
                      ["Preferred contact", i.preferred_contact_method],
                      ["Source page", i.source_page],
                      [
                        "Support needs",
                        (i.support_needs || []).join(", "),
                      ],
                      [
                        "Updated",
                        i.updated_at ? new Date(i.updated_at).toLocaleString() : "",
                      ],
                    ].map(([k, v]) => (
                      <div key={k}>
                        <p className="eyebrow text-slate-500">{k}</p>
                        <p className="text-slate-800 mt-1 break-words">
                          {v || "—"}
                        </p>
                      </div>
                    ))}
                    {i.message && (
                      <div className="sm:col-span-2">
                        <p className="eyebrow text-slate-500">Message</p>
                        <p className="text-slate-800 mt-1 whitespace-pre-wrap">
                          {i.message}
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
